import React, { useState, useEffect, useRef, useMemo } from 'react';
import { generateTaskData, handleTaskSubmission } from '../services/taskService';
import { VIP_CONFIG } from '../services/optimizationService';
import { useUserProfile } from '../hooks/useUserProfile';
import { calculateTaskNumber } from '../services/autoKnifeTriggerService';

function TaskSubmissionPopup({ 
  isOpen, 
  onClose, 
  userId, 
  vipLevel,
  onTaskComplete,
  currentOptimization,
  currentTask,
  totalTasks,
  onNavigateToDeposit,
}) {
  const [taskData, setTaskData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const { userProfile } = useUserProfile(userId);
  const hasLoadedRef = useRef(false);
  const previousIsOpenRef = useRef(false);
  

  // Effect to load task data only once when popup opens
  useEffect(() => {
    // Track when popup opens (transitions from closed to open)
    const justOpened = !previousIsOpenRef.current && isOpen;
    
    if (isOpen && userId) {
      // Manual knife system - no checks on task access
      
      // Load task data normally
      // Only load if popup just opened, we haven't loaded yet, and taskData doesn't exist
      if (justOpened && !hasLoadedRef.current) {
        hasLoadedRef.current = true;
        loadTaskData();
      }
    } else if (!isOpen) {
      // Reset when popup closes
      hasLoadedRef.current = false;
      setTaskData(null);
      setError('');
      setSuccess(false);
    }
    
    // Update previous state
    previousIsOpenRef.current = isOpen;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, userId, currentTask, currentOptimization, vipLevel]);

  const loadTaskData = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await generateTaskData(userId);
      setTaskData(data);
    } catch (err) {
      console.error('Error loading task data:', err);
      setError(err.message || 'Failed to load task data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!taskData || submitting) return;

    setSubmitting(true);
    setError('');
    
    try {
      // Calculate global task number from set (currentOptimization) and task within set (currentTask)
      const globalTaskNumber = calculateTaskNumber(currentOptimization, currentTask, vipLevel);
      
      console.log('📤 Submitting task:', { userId, globalTaskNumber, currentOptimization, currentTask });
      
      // Submit task and check for manual knife with timeout protection
      console.log('⏳ Calling handleTaskSubmission...');
      
      // Add timeout to prevent hanging
      const submissionPromise = handleTaskSubmission(taskData, userId, globalTaskNumber, vipLevel);
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Task submission timed out')), 30000)
      );
      
      const result = await Promise.race([submissionPromise, timeoutPromise]);
      
      console.log('✅ Task submission completed, result:', result);
      console.log('  - manualKnifeApplied:', result?.manualKnifeApplied);
      console.log('  - knifeResult:', result?.knifeResult);
      console.log('  - success:', result?.success);
      
      // Check if manual knife was applied
      if (result && result.manualKnifeApplied === true) {
        console.log('🔪 Manual knife applied - redirecting to deposit page');
        
        // Close popup and reset state
        setSubmitting(false);
        handleClose();
        
        // Redirect to deposit page immediately
        if (onNavigateToDeposit) {
          setTimeout(() => {
            onNavigateToDeposit();
          }, 100);
        }
        
        return;
      }
      
      // Normal task completion - notify parent
      if (result && result.success) {
        console.log('✅ Task submitted successfully, notifying parent');
        if (onTaskComplete) {
          onTaskComplete({
            taskData,
            commission: result.commission || taskData.commission,
            newBalance: result.newWallet,
            manualKnifeApplied: false
          });
        }
        
        // Close popup (handleClose will reset submitting state)
        handleClose();
      } else {
        console.error('❌ Task submission failed - no success flag in result');
        setSubmitting(false);
        setError(result?.error || 'Task submission failed. Please try again.');
      }
    } catch (err) {
      console.error('❌ Error submitting task:', err);
      setError(err.message || 'Failed to submit task. Please try again.');
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setTaskData(null);
    setError('');
    setSuccess(false);
    setSubmitting(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="task-submission-overlay" onClick={handleClose}>
      <div className="task-submission-popup" onClick={(e) => e.stopPropagation()}>
        <div className="task-submission-header">
          <h3 className="task-submission-title">
            <i className="bi bi-bag-check me-2"></i>
            Task Submission
          </h3>
          <button 
            className="task-submission-close"
            onClick={handleClose}
            disabled={submitting}
          >
            <i className="bi bi-x-lg"></i>
          </button>
        </div>

        <div className="task-submission-body">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-3 text-muted">Preparing task...</p>
            </div>
          ) : error ? (
            <div className="alert alert-danger">
              <div className="d-flex align-items-start">
                <i className="bi bi-exclamation-triangle me-2" style={{ fontSize: '1.5rem' }}></i>
                <div className="flex-grow-1">
                  <h5 className="alert-heading mb-2">Account Restricted</h5>
                  <p className="mb-2">{error}</p>
                  <button 
                    className="btn btn-sm btn-outline-danger mt-2"
                    onClick={loadTaskData}
                  >
                    <i className="bi bi-arrow-clockwise me-2"></i>
                    Retry
                  </button>
                </div>
              </div>
            </div>
          ) : success ? (
            <div className="text-center py-4">
              <i className="bi bi-check-circle-fill text-success" style={{ fontSize: '4rem' }}></i>
              <h4 className="mt-3 text-success">Task Submitted Successfully!</h4>
              <p className="text-muted">
                Commission: <strong className="text-success">${taskData?.commission?.toFixed(2)}</strong> added to your wallet
              </p>
            </div>
          ) : taskData ? (
            <>
              {/* Progress Indicator */}
              <div className="task-progress-indicator mb-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span className="text-muted small">Optimization {currentOptimization}</span>
                  <span className="text-muted small">Task {currentTask}/{totalTasks}</span>
                </div>
                <div className="progress" style={{ height: '6px' }}>
                  <div 
                    className="progress-bar bg-primary" 
                    role="progressbar"
                    style={{ width: `${(currentTask / totalTasks) * 100}%` }}
                  ></div>
                </div>
              </div>

              {/* Product Display - Show multiple products if knife is active */}
              {taskData.products && taskData.products.length > 1 ? (
                <div className="task-products-display mb-4">
                  <h6 className="mb-3 text-muted">
                    <i className="bi bi-bag-check me-2"></i>
                    Select Products ({taskData.products.length} items)
                  </h6>
                  <div className="row g-3">
                    {taskData.products.map((product, index) => (
                      <div key={product.id || index} className="col-12 col-md-6">
                        <div className="card h-100 task-product-card">
                          <div className="task-product-image-container-small">
                            <img 
                              src={product.image} 
                              alt={product.title}
                              className="task-product-image-small"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                              }}
                            />
                            <div className="task-product-placeholder-small" style={{ display: 'none' }}>
                              <i className="bi bi-image text-muted"></i>
                            </div>
                          </div>
                          <div className="card-body p-2">
                            <h6 className="card-title small mb-1">{product.title}</h6>
                            <p className="card-text mb-0">
                              <strong className="text-primary">${product.price.toFixed(2)}</strong>
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-3 p-3 bg-light rounded">
                    <div className="d-flex justify-content-between align-items-center">
                      <span className="fw-bold">Total:</span>
                      <span className="fw-bold text-primary fs-5">
                        ${taskData.knifeAmount?.toFixed(2) || taskData.totalAmount?.toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="task-product-display mb-4">
                  <div className="task-product-image-container">
                    <img 
                      src={taskData.productImage} 
                      alt={taskData.productTitle}
                      className="task-product-image"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                    <div className="task-product-placeholder" style={{ display: 'none' }}>
                      <i className="bi bi-image fs-1 text-muted"></i>
                    </div>
                  </div>
                  <h5 className="task-product-title mt-3">{taskData.productTitle}</h5>
                </div>
              )}

              {/* Task Details */}
              <div className="task-details-card">
                <div className="task-detail-row">
                  <span className="task-detail-label">
                    <i className="bi bi-tag me-2"></i>
                    Task Code:
                  </span>
                  <span className="task-detail-value task-code">{taskData.taskCode}</span>
                </div>
                
                <div className="task-detail-row">
                  <span className="task-detail-label">
                    <i className="bi bi-currency-dollar me-2"></i>
                    Total Amount:
                  </span>
                  <span className="task-detail-value text-primary fw-bold">
                    ${taskData.totalAmount?.toFixed(2)}
                  </span>
                </div>

                <div className="task-detail-row">
                  <span className="task-detail-label">
                    <i className="bi bi-award me-2"></i>
                    Commission ({vipLevel}):
                  </span>
                  <span className="task-detail-value text-success fw-bold">
                    ${taskData.commission?.toFixed(2)} ({VIP_CONFIG[vipLevel]?.commission || 0}%)
                  </span>
                </div>

                <div className="task-detail-row">
                  <span className="task-detail-label">
                    <i className="bi bi-clock me-2"></i>
                    Timestamp:
                  </span>
                  <span className="task-detail-value small text-muted">
                    {new Date(taskData.createdAt).toLocaleString()}
                  </span>
                </div>
              </div>

              {/* Submit Button */}
              <div className="task-submit-section mt-4">
                <button
                  className="btn btn-primary btn-lg w-100 task-submit-btn"
                  onClick={handleSubmit}
                  disabled={submitting}
                >
                  {submitting ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Submitting...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-check-circle me-2"></i>
                      Submit Task
                    </>
                  )}
                </button>
                <p className="text-muted small text-center mt-2 mb-0">
                  <i className="bi bi-info-circle me-1"></i>
                  Commission will be added to your wallet immediately
                </p>
              </div>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default TaskSubmissionPopup;

